import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react'
import { Link, NavLink, Navigate, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom'
import LoginPage from './pages/LoginPage'
import RegisterPage from './pages/RegisterPage'
import ForgotPasswordPage from './pages/ForgotPasswordPage'
import ResetPasswordPage from './pages/ResetPasswordPage'
import VerifyEmailPage from './pages/VerifyEmailPage'
import ChangePasswordPage from './pages/ChangePasswordPage'
import Setup2FAPage from './pages/Setup2FAPage'
import DashboardPage from './pages/DashboardPage'
import ConfigPage from './pages/ConfigPage'
import ProfilePage from './pages/ProfilePage'
import PricingPage from './pages/PricingPage'
import UnifiedCheckoutPage from './pages/UnifiedCheckoutPage'
import BillingSuccessPage from './pages/BillingSuccessPage'
import LogsPage from './pages/LogsPage'
import MCPLogsPage from './pages/MCPLogsPage'
import ModelsPage from './pages/ModelsPage'
import KnowledgesPage from './pages/KnowledgesPage'
import SyncPage from './pages/SyncPage'
import IntegrationsPage from './pages/IntegrationsPage'
import ApiKeyPage from './pages/integrations/ApiKeyPage'
import WordPressPage from './pages/integrations/WordPressPage'
import IFramePage from './pages/integrations/IFramePage'
import AgentsPage from './pages/AgentsPage'
import AgentEditPage from './pages/AgentEditPage'
import GlobalConfigPage from './pages/GlobalConfigPage'
import WidgetConfigPage from './pages/WidgetConfigPage'
import PublicAgentConfigPage from './pages/PublicAgentConfigPage'
import BrandingConfigPage from './pages/BrandingConfigPage'
import SendGridConfigPage from './pages/SendGridConfigPage'
import SystemPromptsConfigPage from './pages/SystemPromptsConfigPage'
import PaymentConfigPage from './pages/PaymentConfigPage'
import OllamaConfigPage from './pages/OllamaConfigPage'
import WordPressConfigPage from './pages/WordPressConfigPage'
import OpenAIConfigPage from './pages/OpenAIConfigPage'
import AnthropicConfigPage from './pages/AnthropicConfigPage'
import SendGridTenantConfigPage from './pages/SendGridTenantConfigPage'
import MonitoringPage from './pages/MonitoringPage'
import MCPIntegrations from './pages/MCPIntegrations'
import ConversationsPage from './pages/ConversationsPage'
import EmailConnectionsPage from './pages/EmailConnectionsPage'
import WorkflowsPage from './pages/WorkflowsPage'
import WorkflowEditorPage from './pages/workflows/WorkflowEditorPage'
import WorkflowDetailPage from './pages/workflows/WorkflowDetailPage'
import PlansAdminPage from './pages/PlansAdminPage'
import MemoryPage from './pages/MemoryPage'
import EndUsersPage from './pages/EndUsersPage'
import AdminDashboardPage from './pages/AdminDashboardPage'
import AdminMetricsPage from './pages/AdminMetricsPage'
import AdminUsersPage from './pages/AdminUsersPage'
import AdminProxiesPage from './pages/AdminProxiesPage'
import AdminAffiliatePage from './pages/AdminAffiliatePage'
import AdminOnboardingSettings from './pages/admin/AdminOnboardingSettings'
import AgencyDashboardPage from './pages/AgencyDashboardPage'
import BillingAccountBYOKPage from './pages/BillingAccountBYOKPage'
import WorkspaceSettingsPage from './pages/WorkspaceSettingsPage'
import ErrorBoundary from './components/ErrorBoundary'
import ImpersonationBanner from './components/ImpersonationBanner'
import {
  AccountPage,
  AccountOverview,
  AccountSubscription,
  AccountBilling,
  AccountUsage,
  AccountSettings,
  AccountAffiliatePage,
} from './pages/account'
import ChatWidget from './components/ChatWidget'
import ContactPage from './pages/ContactPage'
import DocumentationPage from './pages/DocumentationPage'
import AffiliateProgramLegalPage from './pages/AffiliateProgramLegalPage'
import LandingPage from './pages/landing/LandingPage'
import MessengerPage from './pages/MessengerPage'
import ChatPage from './pages/messenger/ChatPage'
import CookiesPolicyPage from './pages/CookiesPolicyPage'
import NotFoundPage from './pages/NotFoundPage'
import AppLayout from './components/layout/AppLayout'
import { OnboardingWidget, DiscoveryTooltip } from './components/onboarding'
import { getApiBase, getToken, setThemeClass } from './lib/utils'
import { captureReferralFromUrl } from './lib/referral'
import { isImpersonationToken } from './lib/jwt'
import { useAppLanguage } from './i18n'
import type { Language as LandingLanguage } from './pages/landing/i18n/translations'

export default function App() {
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useAppLanguage()

  // Avant toute autre logique (ex. redirect PWA / → /messenger) : enregistrer ?ref= et envoyer la vue affilié.
  useLayoutEffect(() => {
    void captureReferralFromUrl()
  }, [location.pathname, location.search])

  // Rediriger les PWA installées vers /messenger si elles sont sur la racine (conserver ?ref=, UTM, etc.)
  useEffect(() => {
    // Détecter si c'est une PWA installée (mode standalone)
    const isPWA = window.matchMedia('(display-mode: standalone)').matches || 
                  (window.navigator as any).standalone === true ||
                  document.referrer.includes('android-app://')
    
    // Si PWA installée et sur la page d'accueil (/), rediriger vers Messenger
    if (isPWA && location.pathname === '/') {
      navigate({ pathname: '/messenger', search: location.search }, { replace: true })
    }
  }, [location.pathname, location.search, navigate])
  const [token, setToken] = useState<string | null>(getToken())
  const [user, setUser] = useState<any>(() => {
    try { return JSON.parse(localStorage.getItem('user') || 'null') } catch { return null }
  })
  const [onboardingEnabled, setOnboardingEnabled] = useState<boolean>(true) // Par défaut activé
  const [impersonationActive, setImpersonationActive] = useState<boolean>(() => {
    // Vérifier à la fois le flag localStorage ET le token JWT
    const flag = localStorage.getItem('impersonation_active') === 'true'
    const token = getToken()
    return flag && isImpersonationToken(token)
  })
  const [impersonationTargetEmail, setImpersonationTargetEmail] = useState<string | null>(() => {
    return localStorage.getItem('impersonation_target_email')
  })
  const changelogRaw = useMemo(() => {
    // v1.9.7 - Force cache invalidation with timestamp - v2
    const timestamp = Date.now()
    const modules = import.meta.glob('../CHANGELOG.md', { as: 'raw', eager: true }) as Record<string, string>
    const first = Object.values(modules)[0]
    return typeof first === 'string' ? `${first}<!-- ${timestamp} -->` : ''
  }, [])
  const [appVersion, setAppVersion] = useState<string>('')
  const [brandingLogo, setBrandingLogo] = useState<string | null>(() => {
    try { return localStorage.getItem('branding_logo_url') || null } catch { return null }
  })
  const [currentWorkspaceId, setCurrentWorkspaceId] = useState<number | undefined>(() => {
    try {
      const stored = localStorage.getItem('current_workspace_id')
      return stored ? parseInt(stored, 10) : undefined
    } catch {
      return undefined
    }
  })
  const apiBase = useMemo(() => getApiBase().replace(/\/$/, ''), [])
  const changelogVersion = useMemo(() => extractLatestVersionFromChangelog(changelogRaw), [changelogRaw])
  const displayedVersion = appVersion || changelogVersion || '1.0.0'

  useEffect(() => {
    setThemeClass()
  }, [])

  // Charger le setting onboarding_enabled au démarrage
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const res = await fetch(`${getApiBase()}/settings/onboarding`)
        if (!res.ok) throw new Error('Failed to load onboarding setting')
        const data = await res.json()
        if (!cancelled) {
          setOnboardingEnabled(data.enabled ?? true)
        }
      } catch (err) {
        console.error('Failed to load onboarding setting:', err)
        // En cas d'erreur, garder la valeur par défaut (true)
      }
    })()
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    let cancelled = false

    ;(async () => {
      try {
        const res = await fetch(`${getApiBase()}/health`)
        if (!res.ok) throw new Error('health check failed')
        const data = await res.json()
        if (data?.version && !cancelled) {
          setAppVersion(String(data.version))
          return
        }
        throw new Error('no version in payload')
      } catch {
        if (!cancelled) {
          setAppVersion(changelogVersion || '1.0.0')
        }
      }
    })()

    return () => {
      cancelled = true
    }
  }, [changelogVersion])

  useEffect(() => {
    const handle = (e: StorageEvent) => {
      if (e.key === 'access_token') {
        setToken(getToken())
      } else if (e.key === 'user') {
        try { setUser(JSON.parse(e.newValue || 'null')) } catch { setUser(null) }
      } else if (e.key === 'branding_logo_url') {
        setBrandingLogo(e.newValue || null)
      } else if (e.key === 'impersonation_active' || e.key === 'access_token') {
        // Vérifier à la fois le flag localStorage ET le token JWT
        const flag = localStorage.getItem('impersonation_active') === 'true'
        const token = getToken()
        setImpersonationActive(flag && isImpersonationToken(token))
      } else if (e.key === 'impersonation_target_email') {
        setImpersonationTargetEmail(e.newValue)
      }
    }
    window.addEventListener('storage', handle)
    return () => window.removeEventListener('storage', handle)
  }, [])

  const authed = !!token
  useEffect(() => {
    try {
      const parsed = JSON.parse(localStorage.getItem('user') || 'null')
      setUser(parsed)
    } catch {
      setUser(null)
    }
  }, [token])
  /** Accès aux routes /admin, /models, /logs, etc. : super-utilisateur uniquement (`is_superuser`). */
  const isAdmin = !!user?.is_superuser
  const firstName = useMemo(() => {
    if (user?.first_name) return user.first_name
    if (!user?.email) return 'Profil'
    const local = String(user.email).split('@')[0]
    const parts = local.replace(/\d+/g, '').split(/[._-]+/).filter(Boolean)
    const name = (parts[0] || local)
    return name.charAt(0).toUpperCase() + name.slice(1)
  }, [user])
  const updateBrandingLogo = useCallback((url: string | null) => {
    setBrandingLogo(url)
    try {
      if (url) {
        localStorage.setItem('branding_logo_url', url)
      } else {
        localStorage.removeItem('branding_logo_url')
      }
    } catch {}
    updateFaviconLinks(url)
  }, [])

  const refreshBrandingLogo = useCallback(async () => {
    try {
      const response = await fetch(`${apiBase}/branding/config`)
      if (!response.ok) return
      const data = await response.json()
      const rawUrl = data?.logo_url as string | null | undefined
      if (!rawUrl) {
        updateBrandingLogo(null)
        return
      }
      const normalized = rawUrl.startsWith('/') ? rawUrl : `/${rawUrl}`
      updateBrandingLogo(`${apiBase}${normalized}`)
    } catch {
      // noop
    }
  }, [apiBase, updateBrandingLogo])

  useEffect(() => {
    refreshBrandingLogo()
  }, [refreshBrandingLogo])

  useEffect(() => {
    updateFaviconLinks(brandingLogo)
  }, [brandingLogo])

  const handleWorkspaceChange = useCallback((workspaceId: number) => {
    setCurrentWorkspaceId(workspaceId)
    localStorage.setItem('current_workspace_id', workspaceId.toString())
  }, [])

  const pageTitle = useMemo(() => {
    const path = location.pathname
    const pt = t.pageTitles
    if (path === '/') return pt.dashboard
    if (path === '/conversations') return pt.conversationHistory
    if (path === '/agents') return pt.agents
    if (path === '/knowledges') return pt.knowledges
    if (path === '/media' || path === '/contents') return pt.library
    if (path === '/subjects') return pt.catalog
    if (path === '/global-config') return pt.administration
    if (path === '/global-config/widget') return pt.widgetConfig
    if (path === '/global-config/branding') return pt.branding
    if (path === '/global-config/sendgrid') return pt.sendgrid
    if (path === '/global-config/public-agent') return pt.publicAgent
    if (path === '/global-config/system-prompts') return pt.systemPrompts
    if (path === '/global-config/payment') return pt.paymentStripe
    if (path === '/global-config/ollama') return pt.ollamaCloud
    if (path === '/config') return pt.configuration
    if (path === '/config/wordpress') return pt.wordpress
    if (path === '/config/openai') return pt.openai
    if (path === '/config/anthropic') return pt.anthropic
    if (path === '/config/sendgrid') return pt.sendgridAgent
    if (path === '/users') return pt.users
    if (path === '/models') return pt.models
    if (path === '/integrations') return pt.integrations
    if (path === '/integrations/mcp') return pt.mcp
    if (path === '/monitoring') return pt.monitoring
    if (path === '/logs') return pt.logs
    if (path === '/logs-mcp') return pt.logsMcp
    if (path === '/profile') return pt.profile
    if (path === '/setup-2fa') return pt.twoFA
    if (path === '/workflows') return pt.workflows
    if (path.startsWith('/workflows/')) return pt.workflows
    if (path === '/messenger') return 'Messenger'
    if (path.startsWith('/messenger/')) return 'Messenger'
    if (path === '/admin/plans') return pt.planManagement
    if (path === '/admin/users') return pt.adminUsers
    if (path === '/admin/affiliate') return pt.adminAffiliate
    if (path === '/admin') return pt.administration
    if (path === '/pricing') return pt.pricing
    if (path.startsWith('/account')) return pt.myAccountPage
    if (path === '/memory') return pt.memory
    if (path === '/end-users') return pt.endUsers
    if (path === '/email') return pt.email
    if (path === '/integrations/api-key') return 'API KEY'
    if (path === '/integrations/wordpress') return 'WORDPRESS'
    if (path === '/integrations/iframe') return 'IFRAME'
    return undefined
  }, [location.pathname, location.search, t])

  return (
    <>
      {/* Banner impersonation (si actif) */}
      {authed && impersonationActive && impersonationTargetEmail && (
        <ImpersonationBanner
          adminEmail={user?.email || 'Admin'}
          targetEmail={impersonationTargetEmail}
        />
      )}

      {authed ? (
        <div className={impersonationActive ? 'pt-16' : ''}>
          <AppLayout 
          firstName={firstName} 
          onLogout={() => {
            localStorage.removeItem('access_token');
            localStorage.removeItem('refresh_token');
            localStorage.removeItem('user');
            localStorage.removeItem('current_workspace_id');
            setToken(null);
            setUser(null);
            setCurrentWorkspaceId(undefined);
            navigate('/login');
          }}
          version={displayedVersion}
          pageTitle={pageTitle}
          currentWorkspaceId={currentWorkspaceId}
          onWorkspaceChange={handleWorkspaceChange}
        >
          <Routes>
            <Route path="/" element={<DashboardPage />} />
            {/* Redirections anciennes URLs vers /knowledges */}
            <Route path="/contents" element={<Navigate to="/knowledges" replace />} />
            <Route path="/media" element={<Navigate to="/knowledges" replace />} />
            <Route path="/subjects" element={<Navigate to="/knowledges" replace />} />
            <Route path="/knowledges" element={<KnowledgesPage />} />
            <Route path="/knowledges/sync" element={<SyncPage />} />
            <Route path="/config" element={<ConfigPage />} />
            <Route path="/integrations" element={<IntegrationsPage />} />
            <Route path="/integrations/api-key" element={<ApiKeyPage />} />
            <Route path="/integrations/wordpress" element={<WordPressPage />} />
            <Route path="/integrations/iframe" element={<IFramePage />} />
            <Route path="/integrations/mcp" element={<MCPIntegrations />} />
            <Route path="/agents" element={<AgentsPage />} />
            <Route path="/agents/new" element={<AgentEditPage />} />
            <Route path="/agents/:id/edit" element={<AgentEditPage />} />
            <Route path="/memory" element={<MemoryPage />} />
            <Route path="/end-users" element={<EndUsersPage />} />
            <Route path="/conversations" element={<ConversationsPage />} />
            <Route path="/agency/dashboard" element={<AgencyDashboardPage />} />
            <Route path="/agency/byok" element={<BillingAccountBYOKPage />} />
            <Route path="/email" element={<EmailConnectionsPage />} />
            <Route path="/workflows" element={<WorkflowsPage />} />
            <Route path="/workflows/new" element={<WorkflowEditorPage />} />
            <Route path="/workflows/:id" element={<WorkflowDetailPage />} />
            <Route path="/workflows/:id/edit" element={<WorkflowEditorPage />} />
            <Route path="/messenger" element={<MessengerPage />} />
            <Route path="/messenger/:agentId" element={<ChatPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/profile" element={<ProfilePage />} />
            
            {/* Account Routes — splat requis pour que /account/overview|affiliate|… matchent le layout */}
            <Route path="/account/*" element={<AccountPage />}>
              <Route index element={<Navigate to="/account/overview" replace />} />
              <Route path="overview" element={<AccountOverview />} />
              <Route path="subscription" element={<AccountSubscription />} />
              <Route path="billing" element={<AccountBilling />} />
              <Route path="usage" element={<AccountUsage />} />
              <Route path="affiliate" element={<AccountAffiliatePage />} />
              <Route path="settings" element={<AccountSettings />} />
            </Route>
            <Route path="/setup-2fa" element={<Setup2FAPage />} />
            <Route path="/change-password" element={<ChangePasswordPage />} />
            
            {/* Admin Routes */}
            <Route
              path="/global-config"
              element={isAdmin ? <GlobalConfigPage onBrandingUpdate={updateBrandingLogo} /> : <Navigate to="/" replace />}
            />
            <Route
              path="/global-config/widget"
              element={isAdmin ? <WidgetConfigPage /> : <Navigate to="/" replace />}
            />
            <Route
              path="/global-config/public-agent"
              element={isAdmin ? <PublicAgentConfigPage /> : <Navigate to="/" replace />}
            />
            <Route
              path="/global-config/branding"
              element={isAdmin ? <BrandingConfigPage onBrandingUpdate={updateBrandingLogo} /> : <Navigate to="/" replace />}
            />
            <Route
              path="/global-config/sendgrid"
              element={isAdmin ? <SendGridConfigPage /> : <Navigate to="/" replace />}
            />
            <Route
              path="/global-config/system-prompts"
              element={isAdmin ? <SystemPromptsConfigPage /> : <Navigate to="/" replace />}
            />
            <Route
              path="/global-config/payment"
              element={isAdmin ? <PaymentConfigPage /> : <Navigate to="/" replace />}
            />
            <Route
              path="/global-config/ollama"
              element={isAdmin ? <OllamaConfigPage /> : <Navigate to="/" replace />}
            />
            <Route path="/config/wordpress" element={authed ? <WordPressConfigPage /> : <Navigate to="/" replace />} />
            <Route path="/config/openai" element={authed ? <OpenAIConfigPage /> : <Navigate to="/" replace />} />
            <Route path="/config/anthropic" element={authed ? <AnthropicConfigPage /> : <Navigate to="/" replace />} />
            <Route path="/config/sendgrid" element={authed ? <SendGridTenantConfigPage /> : <Navigate to="/" replace />} />
            <Route path="/logs" element={isAdmin ? <LogsPage /> : <Navigate to="/" replace />} />
            <Route path="/logs-mcp" element={isAdmin ? <MCPLogsPage /> : <Navigate to="/" replace />} />
            {/* /models : Ollama + offre plateforme LLM — super-admin uniquement (API /ollama/* protégée sauf GET /default). */}
            <Route path="/models" element={isAdmin ? <ModelsPage /> : <Navigate to="/" replace />} />
            <Route path="/monitoring" element={isAdmin ? <MonitoringPage /> : <Navigate to="/" replace />} />
            <Route path="/admin" element={isAdmin ? <AdminDashboardPage /> : <Navigate to="/" replace />} />
            <Route path="/admin/plans" element={isAdmin ? <PlansAdminPage /> : <Navigate to="/" replace />} />
            <Route path="/admin/users" element={isAdmin ? <AdminUsersPage /> : <Navigate to="/" replace />} />
            <Route path="/admin/proxies" element={isAdmin ? <AdminProxiesPage /> : <Navigate to="/" replace />} />
            <Route path="/admin/onboarding" element={isAdmin ? <AdminOnboardingSettings /> : <Navigate to="/" replace />} />
            <Route path="/admin/affiliate" element={isAdmin ? <AdminAffiliatePage /> : <Navigate to="/" replace />} />
            
            {/* Workspace Settings Routes */}
            <Route path="/workspace/:workspaceId/settings" element={authed ? <WorkspaceSettingsPage /> : <Navigate to="/" replace />} />
            <Route path="/workspace/:workspaceId/settings/:tab" element={authed ? <WorkspaceSettingsPage /> : <Navigate to="/" replace />} />
            <Route 
              path="/admin/metrics" 
              element={
                isAdmin ? (
                  <ErrorBoundary>
                    <AdminMetricsPage />
                  </ErrorBoundary>
                ) : (
                  <Navigate to="/" replace />
                )
              } 
            />

            {/* Fallback */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </AppLayout>
        </div>
      ) : (
        <div className="min-h-screen flex flex-col">
          <Routes>
            <Route path="/" element={<LocalizedLandingRedirect />} />
            <Route path="/:lang" element={<LocalizedLandingPage />} />
            <Route path="/cookies-policy" element={<CookiesPolicyPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/documentation" element={<DocumentationPage />} />
            <Route path="/legal/affiliate-program" element={<AffiliateProgramLegalPage />} />
            <Route path="/login" element={<LoginPage onLogin={() => setToken(getToken())} />} />
            <Route path="/register" element={<RegisterPage onLogin={() => setToken(getToken())} />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage onLogin={() => setToken(getToken())} />} />
            <Route path="/pricing" element={<Navigate to="/login" />} />
            <Route path="/checkout" element={<UnifiedCheckoutPage />} />
            <Route path="/billing/success" element={<BillingSuccessPage />} />
            <Route path="*" element={<LocalizedLandingRedirect />} />
          </Routes>
        </div>
      )}

{/* Widget chat - visible pour tous (public non connecté + utilisateurs connectés) */}
      <ChatWidget />
      
      {/* Onboarding Widget - visible sur toutes les pages authentifiées si activé */}
      {authed && onboardingEnabled && <OnboardingWidget />}
      {authed && onboardingEnabled && <DiscoveryTooltip />}
    </>
  )
}

const SUPPORTED_LANDING_LANGUAGES: LandingLanguage[] = ['en', 'fr', 'es']

function detectBrowserLandingLanguage(): LandingLanguage {
  if (typeof window === 'undefined') return 'en'
  const browserCode = window.navigator.language?.split('-')[0]?.toLowerCase()
  if (browserCode === 'fr') return 'fr'
  if (browserCode === 'es') return 'es'
  return 'en'
}

function LocalizedLandingRedirect() {
  const language = detectBrowserLandingLanguage()
  return <Navigate to={`/${language}`} replace />
}

function LocalizedLandingPage() {
  const { lang } = useParams<{ lang: string }>()
  const navigate = useNavigate()

  if (!lang || !SUPPORTED_LANDING_LANGUAGES.includes(lang as LandingLanguage)) {
    return <LocalizedLandingRedirect />
  }

  const currentLanguage = lang as LandingLanguage

  useEffect(() => {
    if (typeof document === 'undefined' || typeof window === 'undefined') return

    const baseUrl = window.location.origin
    const canonicalHref = `${baseUrl}/${currentLanguage}`

    const upsertLink = (selector: string, rel: string, href: string, hreflang?: string) => {
      let link = document.head.querySelector<HTMLLinkElement>(selector)
      if (!link) {
        link = document.createElement('link')
        link.setAttribute('data-landing-seo', 'true')
        link.rel = rel
        document.head.appendChild(link)
      }
      link.href = href
      if (hreflang) {
        link.hreflang = hreflang
      } else {
        link.removeAttribute('hreflang')
      }
    }

    upsertLink('link[rel="canonical"][data-landing-seo="true"]', 'canonical', canonicalHref)
    upsertLink('link[rel="alternate"][hreflang="en"][data-landing-seo="true"]', 'alternate', `${baseUrl}/en`, 'en')
    upsertLink('link[rel="alternate"][hreflang="fr"][data-landing-seo="true"]', 'alternate', `${baseUrl}/fr`, 'fr')
    upsertLink('link[rel="alternate"][hreflang="es"][data-landing-seo="true"]', 'alternate', `${baseUrl}/es`, 'es')
    upsertLink(
      'link[rel="alternate"][hreflang="x-default"][data-landing-seo="true"]',
      'alternate',
      `${baseUrl}/en`,
      'x-default'
    )
  }, [currentLanguage])

  return (
    <LandingPage
      initialLanguage={currentLanguage}
      onLanguageChange={(nextLanguage) => {
        navigate(`/${nextLanguage}`, { replace: true })
      }}
    />
  )
}

function extractLatestVersionFromChangelog(raw: string) {
  if (!raw) return null
  
  // Find all versions in the changelog
  const matches = raw.matchAll(/^## \[(\d+\.\d+\.\d+(?:[^\]]*)?)\]/gm)
  const versions = Array.from(matches, m => m[1].split(' ')[0])
  
  if (versions.length === 0) return null
  if (versions.length === 1) return versions[0]
  
  // Compare versions as semver (major.minor.patch)
  const compareVersions = (a: string, b: string): number => {
    const aParts = a.split('.').map(Number)
    const bParts = b.split('.').map(Number)
    
    for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
      const aNum = aParts[i] || 0
      const bNum = bParts[i] || 0
      if (aNum > bNum) return 1
      if (aNum < bNum) return -1
    }
    return 0
  }
  
  // Return the highest version
  return versions.reduce((latest, current) => 
    compareVersions(current, latest) > 0 ? current : latest
  )
}

function updateFaviconLinks(url: string | null) {
  const href = url || '/branding/logo.png'
  const link = document.querySelector<HTMLLinkElement>('link[rel="icon"]')
  const apple = document.querySelector<HTMLLinkElement>('link[rel="apple-touch-icon"]')
  if (link) link.href = href
  if (apple) apple.href = href
}
